import React, { useState, useRef } from "react";
import { PDFDocument } from "pdf-lib";

interface PDFMergerProps {
  onRunRecorded?: (input: Record<string, unknown>, output: unknown) => void;
}

interface FileEntry {
  id: string;
  file: File;
}

export default function PDFMergerTool({ onRunRecorded }: PDFMergerProps) {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultName, setResultName] = useState("merged.pdf");
  const [pageCount, setPageCount] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const newEntries: FileEntry[] = Array.from(files)
      .filter((f) => f.type === "application/pdf" || f.name.endsWith(".pdf"))
      .map((f) => ({ id: crypto.randomUUID(), file: f }));
    setEntries((prev) => [...prev, ...newEntries]);
    setResultUrl(null);
  };

  const remove = (id: string) => setEntries((prev) => prev.filter((e) => e.id !== id));

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    setEntries((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx: number) => {
    setEntries((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const merge = async () => {
    if (entries.length < 2) {
      setMergeError("Please add at least 2 PDF files to merge.");
      return;
    }
    setMerging(true);
    setMergeError(null);
    setResultUrl(null);
    try {
      const mergedPdf = await PDFDocument.create();
      for (const entry of entries) {
        const bytes = await entry.file.arrayBuffer();
        const pdf = await PDFDocument.load(bytes);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((p) => mergedPdf.addPage(p));
      }
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes as unknown as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setResultUrl(url);
      setPageCount(mergedPdf.getPageCount());
      onRunRecorded?.(
        { fileCount: entries.length, fileNames: entries.map((e) => e.file.name) },
        { pageCount: mergedPdf.getPageCount(), sizeBytes: mergedBytes.byteLength },
      );
    } catch (err: any) {
      setMergeError(err?.message || "PDF merge failed. Ensure all files are valid PDFs.");
    } finally {
      setMerging(false);
    }
  };

  const download = () => {
    if (!resultUrl) return;
    const a = document.createElement("a");
    a.href = resultUrl;
    a.download = resultName.endsWith(".pdf") ? resultName : resultName + ".pdf";
    a.click();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-6">
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 hover:border-indigo-400 rounded-xl p-10 text-center cursor-pointer transition"
      >
        <p className="text-gray-500 text-sm">Drop PDF files here or <span className="text-indigo-600 font-medium">click to browse</span></p>
        <p className="text-xs text-gray-400 mt-1">Only PDF files are accepted</p>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {/* File list */}
      {entries.length > 0 && (
        <div className="border rounded-xl divide-y overflow-hidden">
          {entries.map((entry, idx) => (
            <div key={entry.id} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-gray-400 font-mono text-xs w-5 text-right">{idx + 1}</span>
                <span className="text-sm font-medium text-gray-800 truncate">{entry.file.name}</span>
                <span className="text-xs text-gray-400 shrink-0">{(entry.file.size / 1024).toFixed(0)} KB</span>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-4">
                <button onClick={() => moveUp(idx)} disabled={idx === 0} className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-30">↑</button>
                <button onClick={() => moveDown(idx)} disabled={idx === entries.length - 1} className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-30">↓</button>
                <button onClick={() => remove(entry.id)} className="px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Output filename</label>
          <input
            type="text"
            value={resultName}
            onChange={(e) => setResultName(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <div className="sm:self-end">
          <button
            onClick={merge}
            disabled={merging || entries.length < 2}
            className="w-full sm:w-auto bg-indigo-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50 text-sm"
          >
            {merging ? "Merging…" : `Merge ${entries.length} PDFs`}
          </button>
        </div>
      </div>

      {mergeError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{mergeError}</div>
      )}

      {resultUrl && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-green-800">Merge complete!</p>
            <p className="text-sm text-green-700">{pageCount} pages combined from {entries.length} files.</p>
          </div>
          <button
            onClick={download}
            className="bg-green-700 text-white px-5 py-2 rounded-lg font-semibold hover:bg-green-800 transition text-sm shrink-0"
          >
            Download {resultName}
          </button>
        </div>
      )}
    </div>
  );
}
